import { Analytics } from '@util/Analytics';
import { Environment } from '@util/Environment';
import { useQuery, userIdFromQueryParams } from '@util/QueryParams';
import { isDirectToPayRoute } from '@util/RouterUtils';
import React, { ReactElement, useCallback, useEffect } from 'react';
import { isEmpty } from 'ts-utils';
import { ScreenConfigBase, ScreenType } from '../../config/FlowTypes';
import { ActionType } from '../../store/ActionTypes';
import { store, useAppDispatch, useAppStore } from '../../store/Store';
import { DIRECT_MEDICARE_COUPON_CODE, isDirectMedicareFlow } from '../../util/MedicareAccess';
import { StorePersistence } from '../../util/StorePersistance';
import { LoadingSpinner } from '../styles/LoadingSpinner';
import { NavigationProps } from './FlowNavigation';
import { PaymentLayout } from './layouts/PaymentLayout';

export type PaymentScreenConfig<ScreenNames extends string> = ScreenConfigBase<
  ScreenType.Payment,
  ScreenNames
>;

type Props<ScreenNames extends string> = NavigationProps<
  PaymentScreenConfig<ScreenNames>,
  ScreenNames
>;

export const PaymentScreen = <ScreenNames extends string>(
  props: Props<ScreenNames>
): ReactElement => {
  const dispatch = useAppDispatch();
  const query = useQuery();
  const user = useAppStore((state) => state.user);
  const userIdFromQueryString = userIdFromQueryParams(query);
  const userId = user?.userId ?? userIdFromQueryString;
  const userEmail = user?.email;
  const userError = user?.error;
  const userNeedsSetup = !isEmpty(userId) && isEmpty(userEmail);

  useEffect(function persistProgressOnReachingPayment() {
    StorePersistence.persist(store.getState());
  }, []);

  useEffect(function applyDirectMedicareDiscount() {
    // D2C US 65+ users without an attached coupon get the default 20% discount. URL/referral
    // coupons take precedence and must remain intact when the user reaches payment.
    const state = store.getState();
    if (isDirectMedicareFlow(state) === true && state.pricing.couponCode === undefined) {
      dispatch({ type: ActionType.SET_COUPON_CODE, couponCode: DIRECT_MEDICARE_COUPON_CODE });
    }
  }, []);

  useEffect(
    function setupUser() {
      if (userNeedsSetup) {
        // To handle /start flow with userId passed in as a query param
        dispatch({ type: ActionType.SETUP_INCOMPLETE_USER, userId });
      }
    },
    [userNeedsSetup]
  );

  useEffect(
    function identifyUser() {
      // To handle /start flow with userId passed in as a query param
      if (isDirectToPayRoute() && !isEmpty(userId)) {
        Analytics.identify({ userId }, Analytics.Event.o_payment_opened);
      }
    },
    [userId]
  );

  useEffect(
    function goHomeOnAuthError() {
      if (userError !== undefined) {
        window.location.href = Environment.DEVELOPMENT ? `/${Environment.APP}` : '/';
      }
    },
    [userError]
  );

  const onNext = useCallback(() => {
    props.navigation.next(props, true);
  }, [props]);

  return userNeedsSetup ? <LoadingSpinner /> : <PaymentLayout nextPage={onNext} />;
};
